import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toAbsoluteUrl } from '../lib/fileHelpers';
import { 
  Menu, 
  X, 
  LogOut, 
  Bell, 
  Settings, 
  Home,
  ClipboardList,
  Users,
  User,
  ChevronDown,
  Search,
  Shield,
  Map,
  Images,
  Calendar,
  Building2
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useTaskStore } from '../stores/taskStore';
import { useEmployeeStore } from '../stores/employeeStore';
import { useNotificationStore } from '../stores/notificationStore';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);
  const [changeMessage, setChangeMessage] = useState('');
  const [currentOnDuty, setCurrentOnDuty] = useState<any>(null);
  
  const { user, logout, hasPermission, hasFeature } = useAuthStore();
  const { tasks, fetchTasks } = useTaskStore();
  const { fetchEmployees } = useEmployeeStore();
  const { notifications, unreadCount, fetchUnreadNotifications, markAsRead, markAllAsRead } = useNotificationStore();
  
  useEffect(() => {
    fetchTasks();
    fetchEmployees();
  }, [fetchTasks, fetchEmployees]);

  // Fetch current on-duty
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'master')) {
      const fetchOnDuty = async () => {
        try {
          const response = await fetch(`${API_BASE}/on-duty/current`);
          if (response.ok) {
            const data = await response.json();
            setCurrentOnDuty(data);
          }
        } catch (err) {
          console.error('Error fetching on-duty:', err);
        }
      };
      fetchOnDuty();
      
      // Poll every 60 seconds
      const interval = setInterval(fetchOnDuty, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Fetch notifications for admin users every 60 seconds
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'master')) {
      // Initial fetch
      fetchUnreadNotifications(user.id);
      
      // Poll every 60 seconds
      const interval = setInterval(() => {
        fetchUnreadNotifications(user.id);
      }, 60000);
      
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadNotifications]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  const getNewTasksCount = () => {
    if (!user || user.role === 'admin' || user.role === 'master') return 0;
    return tasks.filter(task => 
      task.assignedUsers.includes(user.id) && 
      task.status === 'zakazano'
    ).length;
  };

  const getUrgentTasksCount = () => {
    if (!user) return 0;
    const userTasks = (user.role === 'admin' || user.role === 'master') 
      ? tasks 
      : tasks.filter(task => task.assignedUsers.includes(user.id));
    
    return userTasks.filter(task => 
      task.type === 'hitno' && 
      task.status !== 'zavrseno'
    ).length;
  };

  const getUrgentTasks = () => {
    if (!user) return [];
    const userTasks = (user.role === 'admin' || user.role === 'master') 
      ? tasks 
      : tasks.filter(task => task.assignedUsers.includes(user.id));
    
    return userTasks.filter(task => 
      task.type === 'hitno' && 
      task.status !== 'zavrseno'
    );
  };
  
  const navItems = [
    { 
      name: 'Kontrolna tabla', 
      path: '/dashboard', 
      icon: Home,
      badge: null
    },
    { 
      name: 'Radni nalozi', 
      path: '/tasks',
      icon: ClipboardList,
      badge: getNewTasksCount()
    },
    { 
      name: 'Kalendar', 
      path: '/calendar',
      icon: Calendar,
      badge: null
    },
    ...(hasPermission('manage_employees') ? [{
      name: 'Zaposleni', 
      path: '/employees',
      icon: Users,
      badge: null
    }] : []),
    ...(hasPermission('manage_employees') ? [{
      name: 'Klijenti', 
      path: '/clients',
      icon: Users,
      badge: null
    }] : []),
    ...(hasPermission('manage_employees') ? [{
      name: 'Liftovi', 
      path: '/lifts',
      icon: Building2,
      badge: null
    }] : []),
    ...(hasPermission('manage_employees') ? [{
      name: 'Mapa', 
      path: '/map',
      icon: Map,
      badge: null
    }] : []),
    ...(hasPermission('manage_employees') ? [{
      name: 'Galerija', 
      path: '/gallery',
      icon: Images,
      badge: null
    }] : []),
    ...(user?.role === 'master' ? [{
      name: 'Master Panel', 
      path: '/master',
      icon: Shield,
      badge: null
    }] : []),
    { 
      name: 'Profil', 
      path: '/profile',
      icon: User,
      badge: null
    },
  ];

  const urgentCount = getUrgentTasksCount();
  const urgentTasks = getUrgentTasks();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 transition-all duration-300 ${
        sidebarCollapsed ? 'w-16' : 'w-64'
      } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="flex h-full flex-col bg-gradient-sidebar shadow-sidebar">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-3">
                <img 
                  src="/logo.png" 
                  alt="Belkom Liftovi" 
                  className="h-10 w-auto"
                />
                <span className="text-xs text-gray-400 font-medium">v1.0.0</span>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden md:block p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Urgent notifications */}
          {urgentCount > 0 && !sidebarCollapsed && (
            <div className="mx-4 mb-4 bg-gradient-danger text-white p-3 rounded-xl">
              <div className="flex items-center">
                <Bell className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">
                  {urgentCount} hitnih naloga
                </span>
              </div>
            </div>
          )}
          
          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => 
                    `group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-white/20 text-white shadow-lg'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`
                  }
                >
                  <Icon className={`h-5 w-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'} flex-shrink-0`} />
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1">{item.name}</span>
                      {item.badge && item.badge > 0 && (
                        <span className="bg-primary-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
          
          {/* Notifications - only for admin */}
          {!sidebarCollapsed && (user?.role === 'admin' || user?.role === 'master') && (
            <div className="px-4 py-2 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-300 uppercase">Odbijeni nalozi</h3>
                {unreadCount > 0 && (
                  <span className="text-[10px] text-gray-400">
                    ({unreadCount})
                  </span>
                )}
              </div>
              
              {unreadCount === 0 ? (
                <div className="text-xs text-gray-400 py-2">Nema odbijenih naloga</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {notifications.map(notification => {
                    try {
                      const data = JSON.parse(notification.message);
                      return (
                        <div
                          key={notification.id}
                          className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 cursor-pointer hover:bg-red-500/20 transition-colors"
                          onClick={() => {
                            // Don't mark as read - notification stays until task status changes
                            if (notification.task_id) {
                              navigate(`/tasks/${notification.task_id}`);
                            }
                          }}
                        >
                          <div className="flex items-start gap-2">
                            <Bell className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-red-300 mb-1">
                                {notification.title}
                              </div>
                              <div className="text-xs text-gray-300 space-y-0.5">
                                <div><span className="font-medium">Radnik:</span> {data.workerName}</div>
                                <div><span className="font-medium">Klijent:</span> {data.clientName}</div>
                                <div><span className="font-medium">Razlog:</span> {data.reason}</div>
                              </div>
                              <div className="text-[10px] text-gray-400 mt-1">
                                {new Date(notification.created_at).toLocaleString('sr-RS')}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    } catch (e) {
                      return null;
                    }
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* User info */}
          {!sidebarCollapsed && (
            <div className="p-4 border-t border-white/10">
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-gradient-primary flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {user?.name?.charAt(0)}
                  </span>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <div className="text-white font-medium text-sm truncate">{user?.name}</div>
                  <div className="text-gray-300 text-xs truncate">{user?.position}</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Logout */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-xl transition-colors ${
                sidebarCollapsed ? 'justify-center' : ''
              }`}
            >
              <LogOut className={`h-4 w-4 ${sidebarCollapsed ? '' : 'mr-2'}`} />
              {!sidebarCollapsed && 'Odjavi se'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {/* Header */}
        <header className="bg-white shadow-header border-b border-gray-200 sticky top-0 z-30">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
            
            {/* Header actions */}
            <div className="flex items-center space-x-4 flex-1">
              {/* Current on-duty employee */}
              {(user?.role === 'admin' || user?.role === 'master') && currentOnDuty && (
                <div className="hidden lg:flex items-center gap-3 px-4 py-2">
                  <div className="w-[32px] h-[32px] flex items-center justify-center">
                    <Shield className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="text-xs">
                    <div className="text-gray-600 font-medium mb-0.5">Trenutno dežuran</div>
                    <div className="font-bold text-gray-900 text-sm">{currentOnDuty.employee_name}</div>
                    <div className="text-gray-700 text-xs">
                      {(() => {
                        const formatTimestamp = (ts: string) => {
                          const [date, time] = ts.split('T');
                          const [y, m, d] = date.split('-');
                          const [h, min] = time.split(':');
                          return `${d}.${m}.${y}. ${h}:${min}`;
                        };
                        return `${formatTimestamp(currentOnDuty.start_timestamp)} - ${formatTimestamp(currentOnDuty.end_timestamp)}`;
                      })()}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex-1"></div>

              {/* Current time and date */}
              <div className="text-right">
                <div className="text-sm text-gray-600">
                  {currentTime.toLocaleDateString('sr-RS')} {currentTime.toLocaleTimeString('sr-RS')}
                </div>
              </div>
              
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="p-2 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors relative"
                >
                  <Bell className="h-5 w-5" />
                  {urgentCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-danger-500 text-white text-xs rounded-full flex items-center justify-center">
                      {urgentCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications dropdown */}
                {notificationsOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-floating border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <h3 className="text-sm font-medium text-gray-900">Obaveštenja</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {urgentTasks.length > 0 ? (
                        urgentTasks.map(task => (
                          <div 
                            key={task.id}
                            className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
                            onClick={() => {
                              navigate(`/tasks/${task.id}`);
                              setNotificationsOpen(false);
                            }}
                          >
                            <div className="flex items-center">
                              <div className="h-8 w-8 bg-danger-100 rounded-full flex items-center justify-center">
                                <Bell className="h-4 w-4 text-danger-600" />
                              </div>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-gray-900">
                                  Hitan nalog: {task.client?.name || 'Nepoznat klijent'}
                                </p>
                                <p className="text-xs text-gray-500">{task.location}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center text-gray-500">
                          <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">Nema novih obaveštenja</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-3 p-2 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  {user?.profile_image ? (
                    <img src={toAbsoluteUrl(user.profile_image)} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gradient-primary flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {user?.name?.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium text-gray-900">{user?.name}</div>
                    <div className="text-xs text-gray-500">{user?.position}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </button>
                
                {/* Profile dropdown menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-floating border border-gray-200 py-2 z-50 pointer-events-auto">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">{user?.name}</div>
                      <div className="text-xs text-gray-500">{user?.email}</div>
                    </div>
                    <NavLink
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <User className="h-4 w-4 mr-3" />
                      Profil
                    </NavLink>
                    <button
                      type="button"
                      onClick={() => {
                        setProfileDropdownOpen(false);
                        setShowChangePassword(true);
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                        setChangeMessage('');
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Shield className="h-4 w-4 mr-3" />
                      Promeni lozinku
                    </button>
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-danger-700 hover:bg-danger-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Odjavi se
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 relative">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
      
      {/* Click outside handlers */}
      {(profileDropdownOpen || notificationsOpen) && (
        <div 
          className="fixed inset-0 z-30 pointer-events-none"
          onClick={() => {
            setProfileDropdownOpen(false);
            setNotificationsOpen(false);
          }}
        />
      )}

      {showChangePassword && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={() => setShowChangePassword(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Promeni lozinku</h3>
                {changeMessage && <p className="text-sm text-green-600 mt-1">{changeMessage}</p>}
              </div>
              <button onClick={() => setShowChangePassword(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-700">Trenutna lozinka</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="text-sm text-gray-700">Nova lozinka</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="text-sm text-gray-700">Potvrdi novu lozinku</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowChangePassword(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Otkaži
              </button>
              <button
                type="button"
                disabled={changing}
                onClick={async () => {
                  if (!currentPassword || !newPassword || !confirmPassword) {
                    alert('Popunite sva polja');
                    return;
                  }
                  if (newPassword !== confirmPassword) {
                    alert('Nove lozinke se ne poklapaju');
                    return;
                  }
                  setChanging(true);
                  try {
                    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
                    const resp = await fetch(`${API_BASE_URL}/auth/change-password`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        userId: user?.id,
                        currentPassword,
                        newPassword,
                      })
                    });
                    const respText = await resp.text();
                    let respData: any;
                    try { respData = JSON.parse(respText); } catch {
                      throw new Error('Server nije dostupan. Pokušajte ponovo.');
                    }
                    if (!resp.ok) {
                      throw new Error(respData?.error || 'Greška pri promeni lozinke');
                    }
                    setChangeMessage('Lozinka je uspešno promenjena');
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    // Odmah odjavi da korisnik unese novu lozinku pri sledećem loginu
                    setTimeout(() => {
                      setShowChangePassword(false);
                      handleLogout();
                    }, 800);
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : String(err);
                    alert(msg);
                  } finally {
                    setChanging(false);
                  }
                }}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {changing ? 'Snima...' : 'Sačuvaj'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;